import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Building2 } from "lucide-react";
import { toast } from "sonner";
import LanguageToggle from "../components/LanguageToggle";
import Logo from "../components/Logo";
import { useT } from "../lib/i18n";

const ROLE_HOME = { admin: "/admin", sponsor: "/broker", borrower: "/borrower" };

export default function Login() {
  const { login } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const r = await login(email.trim(), password);
    setBusy(false);
    if (r.ok) {
      toast.success(`Welcome back, ${r.user.name}`);
      const next = loc.state?.from || ROLE_HOME[r.user.role] || "/";
      nav(next, { replace: true });
    } else {
      setErr(r.error);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4 py-10">
      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 60% 60% at 50% 40%, rgba(16,185,129,0.10), transparent 60%)",
        }}
      />
      <div className="relative w-full max-w-md">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-slate-200" data-testid="back-home-link">
          <Logo height={120} />
        </Link>
        <Card className="glass-card border-0 p-8">
          <div className="mb-7">
            <h1 className="font-display text-4xl font-medium tracking-tight">Sign in</h1>
            <p className="mt-1 text-sm text-slate-400">Access your broker portal.</p>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-300 text-[11px] uppercase tracking-[0.18em]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="mt-1.5 border-white/10 bg-black/30 text-slate-50 placeholder:text-slate-500 focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500/40"
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-300 text-[11px] uppercase tracking-[0.18em]">Password</Label>
                <Link to="/forgot-password" className="text-xs text-emerald-300 hover:text-emerald-200" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-1.5 border-white/10 bg-black/30 text-slate-50 placeholder:text-slate-500 focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500/40"
                data-testid="login-password-input"
              />
            </div>
            {err && (
              <div className="rounded-md border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="login-error">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-emerald-600 text-white hover:bg-emerald-500 btn-glow disabled:opacity-60 mt-2"
              data-testid="login-submit-btn"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-7 text-center text-sm text-slate-400">
            New broker?{" "}
            <Link to="/register" className="text-emerald-300 hover:text-emerald-200" data-testid="go-register-link">
              Create an account
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
